import React, { useEffect, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Form, Field, useField } from 'react-final-form';
import { Button, Modal, Glyphicon } from 'react-bootstrap';
import cx from 'classnames';
import WebhookHeadersField from './WebhookHeadersField';
import './CustomAlertEdit.less';

const parseWebhookHeaders = (text = '') => {
  const headers = Object.create(null);
  const names = new Set();
  text.split(/\r?\n/).forEach((line, index) => {
    if (!line.trim()) return;
    const colon = line.indexOf(':');
    const name = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).trim();
    if (colon < 1 || !/^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/.test(name)) {
      throw new Error(`Line ${index + 1}: enter a header name followed by a colon and its value.`);
    }
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1f\x7f]/.test(value)) throw new Error(`Line ${index + 1}: remove control characters from the value.`);
    if (/^(host|content-length|transfer-encoding|connection|content-type|x-courtapi-signature)$/i.test(name)) {
      throw new Error(`Line ${index + 1}: ${name} is managed automatically and cannot be overridden.`);
    }
    if (names.has(name.toLowerCase())) throw new Error(`Line ${index + 1}: ${name} is already listed.`);
    names.add(name.toLowerCase());
    if (value.includes('<fill-in>')) throw new Error(`Line ${index + 1}: replace <fill-in> with your value.`);
    headers[name] = value;
  });
  return headers;
};

const maxEmails = 10;

const intervalOptions = [
  { value: -1, label: 'Save/Pause' },
  { value: 3600, label: 'Hourly' },
  { value: 86400, label: 'Daily' },
  { value: 604800, label: 'Weekly' },
];

const getDeliveryOptions = ({ delivery_options }) => delivery_options;

const getIntervalOptions = ({ notify_interval_options }) => notify_interval_options;

const parseEmailList = mailString => (
  !mailString
    ? []
    : mailString
      .split(new RegExp(/\s*\n|\s*,\s*|\s*;\s*/))
      .filter(f => f !== '')
);

const validateEmailList = (mailListString) => {
  const mailList = parseEmailList(mailListString);

  if (mailList.length > maxEmails) {
    return `May not enter more then ${maxEmails} emails`;
  }

  const invalidEmail = mailList.find(email => !/\S+@\S+\.\S+/.test(email));

  return invalidEmail ? `'${invalidEmail}' doesn't look like a valid email` : null;
};

const onSubmit = (values, onSave, oldAlert) => {
  const newMailList = parseEmailList(values.mail_list);

  const alert = {
    ...values,
    webhook_headers: values.delivery === 'webhook'
      ? parseWebhookHeaders(values.webhook_headers)
      : oldAlert.webhook_headers || {},
    mail_list: newMailList.length > 0 ? newMailList : oldAlert.mail_list,
  };

  onSave(alert);
};

const hasError = meta => meta.error && meta.submitFailed;

const generateSigningSecret = () => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let secret = 'whsec_';
  while (secret.length < 38) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(32));
    for (const byte of bytes) {
      // Reject the uneven remainder so all 62 characters are equally likely.
      if (byte < 248 && secret.length < 38) secret += alphabet[byte % 62];
    }
  }
  return secret;
};

const WebhookSecretField = () => {
  const { input, meta } = useField('webhook_secret', {
    parse: value => value,
    validate: value => (value ? undefined : 'Webhook signing secret is required'),
  });
  const [visible, setVisible] = useState(() => !input.value);
  const [copied, setCopied] = useState(false);
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (!input.value) {
      input.onChange(generateSigningSecret());
    }
  }, [input.value, input.onChange]);
  useEffect(() => setCopied(false), [input.value]);
  return (
    <div className="form-item">
      <label htmlFor="edit-webhook-secret">Webhook signing secret:</label>
      <div className="input-group">
        <input {...input} id="edit-webhook-secret" type={visible ? 'text' : 'password'}
          autoComplete="new-password" readOnly className="form-control" style={{ fontSize: '12px', fontFamily: 'monospace' }} />
        <span className="input-group-btn">
          <Button type="button" onClick={() => setVisible(!visible)}
            aria-label={visible ? 'Hide webhook signing secret' : 'Show webhook signing secret'}
            title={visible ? 'Hide webhook signing secret' : 'Show webhook signing secret'}>
            <Glyphicon glyph={visible ? 'eye-close' : 'eye-open'} />
          </Button>
          <Button type="button" onClick={() => setCopied(copy(input.value))}>{copied ? 'Copied' : 'Copy'}</Button>
          <Button type="button" onClick={() => {
        input.onChange(generateSigningSecret());
        setVisible(true);
          }}>Regenerate</Button>
        </span>
      </div>
      <div className="description">
        <a href="https://www.courtapi.com/docs/api#tag/webhooks/Verify-signatures" target="_blank" rel="noopener noreferrer">
          Learn more about validating webhooks
        </a>
      </div>
      {hasError(meta) && <span className="error description">{meta.error}</span>}
    </div>
  );
};

export const CustomAlertEdit = ({ alert, onSave, show, onClose }) => (
  <Modal show={show} onHide={onClose} backdrop="static" keyboard={false}>
    <div className="custom-alert-edit">
      <Form
        onSubmit={values => onSubmit(values, onSave, alert)}
        initialValues={{
          ...alert,
          webhook_secret: alert.webhook_secret || '',
          webhook_headers: Object.keys(alert.webhook_headers || {}).map(name => `${name}: ${alert.webhook_headers[name]}`).join('\n'),
          mail_list: alert.mail_list && alert.mail_list.length > 0 ? alert.mail_list.join('\n') : '',
        }}
        validate={(values) => {
          const errors = {};

          if (!values.name) {
            errors.name = 'Name is required';
          }

          if (!getDeliveryOptions(alert).some(option => option.value === values.delivery)) {
            errors.delivery = 'Notification Delivery is required';
          }

          if (values.delivery !== 'webhook') {
            if (!intervalOptions.some(option => option.value.toString() === values.notify_interval.toString())) {
              errors.notify_interval = 'Notification Interval is required';
            }

            if (!values.mail_list) {
              errors.mail_list = 'Distribution list is required';
            }
          }

          if (values.delivery === 'webhook' && !values.webhook_url) {
            errors.webhook_url = 'Webhook URL is required';
          }

          if (values.delivery === 'webhook') {
            try { parseWebhookHeaders(values.webhook_headers); }
            catch (error) { errors.webhook_headers = error.message; }
          }

          if (values.mail_list) {
            const errorMessage = validateEmailList(values.mail_list);

            if (errorMessage) {
              errors.mail_list = errorMessage;
            }
          }

          return errors;
        }}
        render={({ handleSubmit, values /* , submitting, pristine, form */ }) => (
          <form onSubmit={handleSubmit} id="ir-alerts-edit-form">
            <Modal.Header closeButton>
              <Modal.Title>
                Edit Alert
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <Field name="name">
                  {({ input, meta }) => {
                    const errorClass = cx({ error: hasError(meta) });

                    return (
                      <div className="form-item" id="edit-name-wrapper">
                        <label htmlFor="edit-name" className={errorClass}>
                          Name:
                          <span className="form-required" title="This field is required.">*</span>
                        </label>
                        <input
                          {...input}
                          type="text"
                          id="edit-name"
                          maxLength="50"
                          className={`form-text ${errorClass}`}
                          placeholder="The name that will be displayed for this saved search"
                        />
                        {hasError(meta) && <span className="error description">{meta.error}</span>}
                      </div>
                    );
                  }}
                </Field>
                <Field
                  name="delivery"
                  parse={({ value }) => value}
                >
                  {({ input: { onChange, ...rest }, meta }) => {
                    const errorClass = cx({ error: hasError(meta) });

                    return (
                      <div className="form-item" id="edit-delivery-wrapper">
                        <label htmlFor="edit-delivery" className={errorClass}>
                          Notification delivery:
                          <span className="form-required" title="This field is required.">*</span>
                        </label>
                        <Select
                          {...rest}
                          onChange={({ label, value }) => {
                            if (value !== 'webhook') {
                              if (values.webhook_url) {
                                values.webhook_url = '';
                              }

                              if (!intervalOptions.some(option => option.value === values.notify_interval)) {
                                values.notify_interval = intervalOptions[0].value;
                              }
                            }

                            onChange({ label, value });
                          }}
                          options={getDeliveryOptions(alert)}
                          clearable={false}
                          id="edit-delivery"
                          className={`form-select ${errorClass}`}
                        />
                        {hasError(meta) && <span className="error description">{meta.error}</span>}
                      </div>
                    );
                  }}
                </Field>
                {values.delivery !== 'webhook' &&
                  <Field name="notify_interval" >
                    {({ input: { onChange, ...rest }, meta }) => {
                      const errorClass = cx({ error: hasError(meta) });

                      return (
                        <div className="form-item" id="edit-notify-interval-wrapper">
                          <label htmlFor="edit-notify-interval" className={errorClass}>
                            Notification interval:
                            <span className="form-required" title="This field is required.">*</span>
                          </label>
                          <Select
                            {...rest}
                            onChange={({ value }) => {
                              onChange(value);
                            }}
                            options={getIntervalOptions(alert)}
                            clearable={false}
                            id="edit-notify-interval"
                            className={`form-select ${errorClass}`}
                          />
                          {hasError(meta) && <span className="error description">{meta.error}</span>}
                        </div>
                      );
                    }}
                  </Field>
                }
                {values.delivery === 'webhook' &&
                  <Field name="webhook_url">
                    {({ input, meta }) => {
                      const errorClass = cx({ error: hasError(meta) });

                      return (
                        <div className="form-item" id="edit-webhook-url-wrapper">
                          <label htmlFor="edit-webhook-url" className={errorClass}>
                            Webhook URL:
                            <span className="form-required" title="This field is required.">*</span>
                          </label>
                          <input
                            {...input}
                            type="text"
                            id="edit-webhook-url"
                            maxLength="128"
                            className={`form-text ${errorClass}`}
                            placeholder="The URL to which the payload will be delivered"
                          />
                          {hasError(meta) && <span className="error description">{meta.error}</span>}
                        </div>
                      );
                    }}
                  </Field>
                }
                {values.delivery === 'webhook' && <div>
                  <WebhookSecretField />
                  <WebhookHeadersField />
                </div>}
                {values.delivery !== 'webhook' &&
                  <Field name="mail_list">
                    {({ input, meta }) => {
                      const errorClass = cx({ error: hasError(meta) });

                      return (
                        <div className="form-item" id="edit-emails-wrapper">
                          <label htmlFor="edit-emails" className={errorClass}>
                            Send alert emails to the following distribution list:
                            <span className="form-required" title="This field is required.">*</span>
                          </label>
                          <textarea
                            {...input}
                            id="edit-emails"
                            rows="4"
                            className={`form-textarea ${errorClass}`}
                            placeholder={
                              `Add up to ${maxEmails} emails, one per line or separated by commas or semicolons`
                            }
                          />
                          {hasError(meta) && <span className="error description">{meta.error}</span>}
                        </div>
                      );
                    }}
                  </Field>
                }
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={onClose}>
                Cancel
              </Button>
              <Button bsStyle="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </div>
  </Modal>
);

CustomAlertEdit.propTypes = {
  alert: PropTypes.any,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
};

CustomAlertEdit.defaultProps = {
  alert: {},
  show: false,
};

export default CustomAlertEdit;



// WEBPACK FOOTER //
// ./src/components/searchAlerts/CustomAlerts/CustomAlertEdit.jsx